<template>
  <div>
    <v-dialog
      :value="show"
      max-width="580"
      scrollable
      persistent
      @input="onDialogInput"
    >
      <v-card rounded="xl" class="overflow-hidden">
        <v-card-title class="d-flex align-center text-h6 font-weight-bold">
          <span>醫師照片設定</span>
          <v-spacer />
          <v-btn icon aria-label="關閉" :disabled="saving" @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-progress-linear
          v-if="loadingSettings || saving"
          indeterminate
          color="teal lighten-2"
          class="ma-0"
        />

        <v-divider />

        <v-card-text class="pa-4 pa-sm-6">
          <template v-if="settingsLoaded">
            <div class="text-subtitle-1 font-weight-bold mb-4">主要照片</div>
            <div class="d-flex align-start align-sm-center">
              <v-avatar size="88" color="grey lighten-4" class="flex-shrink-0 mr-4">
                <img v-if="mainPreviewUrl" :src="mainPreviewUrl" alt="主要照片">
                <v-icon v-else color="grey lighten-1" size="34">mdi-account</v-icon>
              </v-avatar>
              <div class="flex-grow-1">
                <div class="text-caption grey--text text--darken-1 mb-2">
                  顯示於醫師個人頁面，並作為各診所未個別設定時的預設照片
                </div>
                <div class="d-flex flex-wrap">
                  <v-btn
                    color="teal lighten-2"
                    dark
                    small
                    class="rounded-lg mr-2 mb-2"
                    :disabled="saving"
                    @click="openMainUploader"
                  >
                    上傳／更換照片
                  </v-btn>
                  <v-btn
                    outlined
                    small
                    class="rounded-lg mr-2 mb-2"
                    :disabled="saving"
                    @click="removeMainPhoto"
                  >
                    移除照片
                  </v-btn>
                </div>
              </div>
            </div>

            <v-divider class="my-6" />

            <div class="d-flex align-center justify-space-between mb-3">
              <span class="text-subtitle-1 font-weight-bold">各診所照片</span>
              <span class="text-caption grey--text">共{{ clinics.length }}家診所</span>
            </div>

            <v-card
              v-for="clinic in clinics"
              :key="clinicKey(clinic)"
              outlined
              rounded="lg"
              class="mb-3 overflow-hidden"
              :class="{'clinic-card--open': openClinic === clinicKey(clinic)}"
              :data-clinic-id="clinicKey(clinic)"
            >
              <div
                class="clinic-row d-flex align-center px-4 py-3"
                role="button"
                tabindex="0"
                @click="toggleClinic(clinic)"
                @keydown.enter="toggleClinic(clinic)"
              >
                <v-avatar size="48" color="grey lighten-4" class="flex-shrink-0 mr-3">
                  <img
                    v-if="clinicPreviewUrl(clinic)"
                    :src="clinicPreviewUrl(clinic)"
                    alt="診所照片"
                  >
                  <span v-else class="text-caption grey--text">待上傳</span>
                </v-avatar>
                <div class="flex-grow-1 text-body-1 font-weight-medium text-truncate">
                  {{ clinic.clinic_name }}
                </div>
                <v-chip
                  small
                  class="flex-shrink-0 mr-2"
                  :color="statusColor(clinic.doctor_photo_mode)"
                  :text-color="statusTextColor(clinic.doctor_photo_mode)"
                >
                  {{ statusLabel(clinic.doctor_photo_mode) }}
                </v-chip>
                <v-tooltip
                  v-if="showMainPhotoFallbackTooltip(clinic)"
                  top
                  max-width="420"
                >
                  <template #activator="{ on, attrs }">
                    <v-icon
                      color="amber darken-2"
                      small
                      class="flex-shrink-0 mr-2"
                      v-bind="attrs"
                      v-on="on"
                      @click.stop
                    >
                      mdi-alert-circle
                    </v-icon>
                  </template>
                  <span>目前尚未上傳主要照片，此診所會顯示系統預設圖</span>
                </v-tooltip>
                <v-icon color="grey" small class="flex-shrink-0">
                  {{ openClinic === clinicKey(clinic) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                </v-icon>
              </div>

              <v-expand-transition>
                <div v-if="openClinic === clinicKey(clinic)">
                  <v-divider />
                  <div class="px-3 pt-2 pb-3">
                    <v-radio-group
                      v-model="clinic.doctor_photo_mode"
                      hide-details
                      class="ma-0"
                    >
                      <template v-for="mode in modes">
                        <v-radio
                          :key="'radio-' + mode.value"
                          :value="mode.value"
                          class="mb-2"
                        >
                          <template #label>
                            <div>
                              <div class="text-body-2 font-weight-medium">{{ mode.label }}</div>
                              <div class="text-caption grey--text">{{ mode.description }}</div>
                            </div>
                          </template>
                        </v-radio>

                        <v-card
                          v-if="clinic.doctor_photo_mode === mode.value && mode.value === PHOTO_MODE.OWN"
                          :key="'upload-' + mode.value"
                          outlined
                          rounded="lg"
                          class="clinic-upload-box d-flex flex-column align-center justify-center text-center pa-3 ml-9"
                          @click.stop="openClinicUploader(clinic)"
                        >
                          <v-img
                            v-if="clinicUploadPreviewUrl(clinic)"
                            :src="clinicUploadPreviewUrl(clinic)"
                            max-width="100%"
                            max-height="90"
                            contain
                            rounded="lg"
                            alt="診所專屬照片"
                          />
                          <template v-else>
                            <v-icon color="grey lighten-1">mdi-cloud-upload-outline</v-icon>
                            <span class="text-caption grey--text">點擊或拖拉上傳此診所專屬照片</span>
                          </template>
                          <span
                            v-if="clinicUploadPreviewUrl(clinic)"
                            class="text-caption teal--text"
                          >
                            點擊更換
                          </span>
                        </v-card>
                      </template>
                    </v-radio-group>
                  </div>
                </div>
              </v-expand-transition>
            </v-card>
          </template>

          <div v-else-if="!loadingSettings" class="pa-12 text-center grey--text">
            <v-icon color="grey lighten-1" size="36">mdi-alert-circle-outline</v-icon>
            <div class="mt-3">照片設定載入失敗</div>
            <v-btn text color="teal" class="mt-2" @click="loadSettings">
              重新載入
            </v-btn>
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn outlined class="rounded-lg" :disabled="saving" @click="close">
            取消
          </v-btn>
          <v-btn
            color="teal lighten-2"
            dark
            class="rounded-lg"
            :loading="saving"
            :disabled="!settingsLoaded || loadingSettings"
            @click="onSave"
          >
            儲存
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <upload-image-dialog
      ref="imageUploader"
      hide-activator
      :disabled="saving"
      @submit="onUploadFile"
    />

    <v-dialog v-model="missingPhotoDialog" max-width="430" persistent>
      <v-card>
        <v-card-title class="font-weight-bold">尚未上傳專屬照片</v-card-title>
        <v-card-text>
          以下診所已選擇「使用診所專屬照片」，但尚未上傳照片：
          <strong>{{ missingClinics.map(clinic => clinic.clinic_name).join('、') }}</strong>
          <br>
          請返回上傳照片，或取消這些診所的專屬設定。
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="useMainPhotoForMissingClinics">取消專屬設定</v-btn>
          <v-btn color="teal lighten-2" dark @click="backToUpload">返回上傳</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="mainPhotoConfirmDialog" max-width="430" persistent>
      <v-card>
        <v-card-text class="pt-6">
          儲存後，主要照片會同時影響 {{ affectedClinicCount }} 家「使用主要照片」的診所，確定儲存？
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="mainPhotoConfirmDialog = false">再想想</v-btn>
          <v-btn color="teal lighten-2" dark @click="confirmMainPhotoSave">確定儲存</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="discardDialog" max-width="430" persistent>
      <v-card>
        <v-card-text class="pt-6">
          尚未儲存的變更會消失，確定要關閉嗎？
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="discardDialog = false">繼續編輯</v-btn>
          <v-btn color="error" dark @click="confirmDiscard">捨棄變更</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import UploadImageDialog from '@/components/utils/UploadImageDialog.vue';
import {
  getDoctorPhotoSettingsAPI,
  updateDoctorPhotoSettingsAPI
} from '@/api';

const PHOTO_MODE = Object.freeze({
  MAIN: 0,
  OWN: 1,
  DEFAULT: 2
});

export default {
  name: 'DoctorPhotoSettingsDialog',

  components: {UploadImageDialog},

  props: {
    show: {
      type: Boolean,
      default: false
    },
    doctor: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      PHOTO_MODE,
      loadingSettings: false,
      settingsLoaded: false,
      saving: false,
      openClinic: null,
      uploadTarget: null,
      missingPhotoDialog: false,
      mainPhotoConfirmDialog: false,
      discardDialog: false,
      missingClinics: [],
      affectedClinicCount: 0,
      defaultProfileUrl: '',
      main: {
        previewUrl: '',
        file: null,
        removed: false,
        changed: false,
        profile: ''
      },
      clinics: [],
      modes: [
        {
          value: PHOTO_MODE.MAIN,
          label: '使用主要照片',
          description: '此診所套用主要照片，主要照片更新時一併變更'
        },
        {
          value: PHOTO_MODE.OWN,
          label: '使用診所專屬照片',
          description: '為此診所設定專屬照片'
        },
        {
          value: PHOTO_MODE.DEFAULT,
          label: '移除照片',
          description: '此診所不顯示本人照片，改以系統預設圖顯示'
        }
      ]
    };
  },

  computed: {
    ...mapGetters(['axiosParamData']),
    hasDraftChanges() {
      return this.main.changed || this.clinics.some(clinic => {
        return clinic.doctor_photo_mode !== clinic.initialMode
          || Boolean(clinic.doctor_profile_file);
      });
    },
    mainPreviewUrl() {
      if (this.main.previewUrl) {
        return this.main.previewUrl;
      }
      if (this.main.removed) {
        return this.defaultProfileUrl;
      }
      return this.doctor.profile || '';
    }
  },

  watch: {
    show: {
      immediate: true,
      handler(show) {
        if (show) {
          this.loadSettings();
        } else {
          this.resetDraft();
        }
      }
    }
  },

  beforeDestroy() {
    this.clearObjectUrls();
  },

  methods: {
    async loadSettings() {
      this.loadingSettings = true;
      this.settingsLoaded = false;
      try {
        const response = await getDoctorPhotoSettingsAPI(this.axiosParamData);
        this.applySettings(response.data);
      } catch (error) {
        this.$notify.error('照片設定載入失敗，請再試一次');
      } finally {
        this.loadingSettings = false;
      }
    },

    applySettings(data) {
      this.clearObjectUrls();
      const settings = data.settings || data;
      const clinics = Array.isArray(settings.clinics) ? settings.clinics : [];
      const normalizedClinics = clinics.map(clinic => {
        const mode = this.normalizeMode(clinic.doctor_photo_mode);
        return {
          ...clinic,
          doctor_photo_mode: mode,
          initialMode: mode,
          initialDoctorProfile: clinic.doctor_profile || '',
          doctor_profile_file: null,
          objectUrl: '',
          hasExistingOwnPhoto: mode === PHOTO_MODE.OWN && Boolean(
            clinic.doctor_profile || clinic.doctor_profile_url
          )
        };
      });

      this.defaultProfileUrl = this.getDefaultProfileUrl(settings, normalizedClinics);
      this.main = {
        previewUrl: settings.main_effective_profile || settings.main_profile_url || '',
        file: null,
        removed: this.toBoolean(settings.main_profile_removed),
        changed: false,
        profile: settings.main_profile || ''
      };
      this.affectedClinicCount = Number(settings.main_photo_affected_clinic_count || 0);
      this.clinics = normalizedClinics;
      this.openClinic = null;
      this.settingsLoaded = true;
    },

    getDefaultProfileUrl(settings, clinics) {
      if (this.toBoolean(settings.main_profile_removed)) {
        return settings.main_effective_profile || '';
      }
      const defaultClinic = clinics.find(clinic => clinic.initialMode === PHOTO_MODE.DEFAULT);
      return defaultClinic ? defaultClinic.effective_profile || '' : '';
    },

    normalizeMode(mode) {
      const normalized = Number(mode);
      return Object.values(PHOTO_MODE).includes(normalized) ? normalized : PHOTO_MODE.MAIN;
    },

    toBoolean(value) {
      return value === true || value === 1 || value === '1' || value === 'true';
    },

    clinicKey(clinic) {
      return clinic.doctor_in_clinic_id || clinic.clinic_code;
    },

    statusLabel(mode) {
      const currentMode = this.modes.find(item => item.value === this.normalizeMode(mode));
      return currentMode ? currentMode.label : '';
    },

    statusColor(mode) {
      const normalizedMode = this.normalizeMode(mode);
      if (normalizedMode === PHOTO_MODE.OWN) {
        return 'teal lighten-2';
      }
      return normalizedMode === PHOTO_MODE.DEFAULT
        ? 'amber lighten-5'
        : 'teal lighten-5';
    },

    statusTextColor(mode) {
      if (this.normalizeMode(mode) === PHOTO_MODE.OWN) {
        return 'white';
      }
      return this.normalizeMode(mode) === PHOTO_MODE.DEFAULT
        ? 'amber darken-3'
        : 'teal darken-1';
    },

    showMainPhotoFallbackTooltip(clinic) {
      return this.normalizeMode(clinic.doctor_photo_mode) === PHOTO_MODE.MAIN
        && (!this.main.profile || this.main.removed)
        && !this.main.file;
    },

    clinicPreviewUrl(clinic) {
      const mode = this.normalizeMode(clinic.doctor_photo_mode);
      if (mode === PHOTO_MODE.MAIN) {
        return this.mainPreviewUrl;
      }
      if (mode === PHOTO_MODE.OWN) {
        return this.clinicUploadPreviewUrl(clinic);
      }
      return clinic.initialMode === PHOTO_MODE.DEFAULT
        ? clinic.effective_profile || this.defaultProfileUrl
        : this.defaultProfileUrl;
    },

    clinicUploadPreviewUrl(clinic) {
      if (clinic.objectUrl) {
        return clinic.objectUrl;
      }
      if (clinic.hasExistingOwnPhoto) {
        return clinic.doctor_profile_url || clinic.effective_profile || '';
      }
      return '';
    },

    toggleClinic(clinic) {
      const key = this.clinicKey(clinic);
      this.openClinic = this.openClinic === key ? null : key;
    },

    openMainUploader() {
      this.uploadTarget = {type: 'main'};
      this.$refs.imageUploader.open();
    },

    openClinicUploader(clinic) {
      this.uploadTarget = {type: 'clinic', key: this.clinicKey(clinic)};
      this.$refs.imageUploader.open();
    },

    onUploadFile(file) {
      if (!this.uploadTarget) {
        return;
      }

      if (this.uploadTarget.type === 'main') {
        this.revokeObjectUrl(this.main.previewUrl);
        this.main.file = file;
        this.main.previewUrl = URL.createObjectURL(file);
        this.main.removed = false;
        this.main.changed = true;
      } else {
        const clinic = this.clinics.find(item => this.clinicKey(item) === this.uploadTarget.key);
        if (clinic) {
          this.revokeObjectUrl(clinic.objectUrl);
          clinic.doctor_profile_file = file;
          clinic.objectUrl = URL.createObjectURL(file);
          clinic.doctor_photo_mode = PHOTO_MODE.OWN;
        }
      }
      this.uploadTarget = null;
    },

    removeMainPhoto() {
      this.revokeObjectUrl(this.main.previewUrl);
      this.main.file = null;
      this.main.previewUrl = '';
      this.main.removed = true;
      this.main.changed = true;
    },

    onSave() {
      if (this.saving) {
        return;
      }

      this.missingClinics = this.clinics.filter(clinic => {
        return this.normalizeMode(clinic.doctor_photo_mode) === PHOTO_MODE.OWN
          && !this.clinicUploadPreviewUrl(clinic);
      });

      if (this.missingClinics.length) {
        this.missingPhotoDialog = true;
        return;
      }

      if (this.main.changed && this.affectedClinicCount > 0) {
        this.mainPhotoConfirmDialog = true;
        return;
      }

      this.submitSettings();
    },

    backToUpload() {
      const clinic = this.missingClinics[0];
      this.missingPhotoDialog = false;
      if (!clinic) {
        return;
      }
      this.openClinic = this.clinicKey(clinic);
      this.$nextTick(() => {
        const element = this.$el.querySelector(`[data-clinic-id="${this.clinicKey(clinic)}"]`);
        if (element) {
          element.scrollIntoView({behavior: 'smooth', block: 'center'});
        }
      });
    },

    useMainPhotoForMissingClinics() {
      this.missingClinics.forEach(clinic => {
        clinic.doctor_photo_mode = PHOTO_MODE.MAIN;
      });
      this.missingPhotoDialog = false;
      this.$nextTick(() => this.onSave());
    },

    confirmMainPhotoSave() {
      this.mainPhotoConfirmDialog = false;
      this.submitSettings();
    },

    async submitSettings() {
      this.saving = true;
      try {
        const response = await updateDoctorPhotoSettingsAPI(this.buildFormData());
        const settings = response.data.settings || response.data;
        this.applySettings(settings);
        this.$emit('saved', settings);
        this.$emit('update:show', false);
      } catch (error) {
        this.$notify.error(this.errorMessage(error));
      } finally {
        this.saving = false;
      }
    },

    buildFormData() {
      const formData = new FormData();
      Object.entries(this.axiosParamData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, value);
        }
      });

      if (this.main.file) {
        formData.append('main_profile', this.main.file);
      }
      if (this.main.removed) {
        formData.append('remove_main_profile', '1');
      }

      this.clinics.forEach((clinic, index) => {
        const prefix = `clinics[${index}]`;
        const mode = this.normalizeMode(clinic.doctor_photo_mode);
        formData.append(`${prefix}[clinic_code]`, clinic.clinic_code);
        formData.append(`${prefix}[doctor_photo_mode]`, mode);

        if (mode === PHOTO_MODE.OWN && clinic.doctor_profile_file) {
          formData.append(`${prefix}[doctor_profile_file]`, clinic.doctor_profile_file);
        } else if (mode === PHOTO_MODE.OWN && clinic.initialDoctorProfile) {
          formData.append(`${prefix}[doctor_profile]`, clinic.initialDoctorProfile);
        }

        if (mode !== PHOTO_MODE.OWN && clinic.initialDoctorProfile) {
          formData.append(`${prefix}[remove_clinic_profile]`, '1');
        }
      });

      return formData;
    },

    errorMessage(error) {
      const message = error.response && error.response.data
        ? error.response.data.messages
        : '';
      const messages = {
        doctor_profile_required: '請先上傳診所專屬照片，或取消該診所的專屬設定',
        invalid_doctor_photo_mode: '診所照片模式不正確，請重新選擇',
        clinic_not_found: '找不到指定的診所，請重新載入後再試',
        doctor_not_found: '找不到醫師資料，請重新載入後再試',
        param_error: '照片設定資料不完整，請重新載入後再試',
        failed: '照片設定儲存失敗，請再試一次'
      };
      return messages[message] || '照片設定儲存失敗，請再試一次';
    },

    onDialogInput(value) {
      if (!value) {
        this.close();
      }
    },

    close() {
      if (this.saving) {
        return;
      }
      if (this.hasDraftChanges) {
        this.discardDialog = true;
        return;
      }
      this.$emit('update:show', false);
    },

    confirmDiscard() {
      this.discardDialog = false;
      this.$emit('update:show', false);
    },

    resetDraft() {
      this.clearObjectUrls();
      this.settingsLoaded = false;
      this.openClinic = null;
      this.uploadTarget = null;
      this.missingPhotoDialog = false;
      this.mainPhotoConfirmDialog = false;
      this.discardDialog = false;
      this.missingClinics = [];
      this.clinics = [];
    },

    clearObjectUrls() {
      this.revokeObjectUrl(this.main.previewUrl);
      this.clinics.forEach(clinic => this.revokeObjectUrl(clinic.objectUrl));
    },

    revokeObjectUrl(url) {
      if (url && url.indexOf('blob:') === 0) {
        URL.revokeObjectURL(url);
      }
    }
  }
};
</script>

<style scoped>
.clinic-card--open {
  border-color: #3cb7a7;
}

.clinic-row {
  cursor: pointer;
}

.clinic-upload-box {
  min-height: 62px;
  border-width: 2px;
  border-style: dashed;
  cursor: pointer;
}

.clinic-upload-box:hover {
  border-color: #3cb7a7;
  color: #2fa292;
}
</style>
